import { ref, computed, watch } from 'vue';

import useTableRouter, {
  type ParamsFromQueryFn,
  type ParamsToQueryFn,
} from '../useTableRouter';
import type { SearchParams } from './functions';

import { SortOrder } from '@/graphql/generated/graphql';

export default function useTableSearchState<
  QueryParams extends SearchParams,
  PaginationParams extends SearchParams,
>(
  getParamsToQuery: ParamsToQueryFn<QueryParams, PaginationParams>,
  getParamsFromQuery: ParamsFromQueryFn<PaginationParams>,
  setURL?: boolean,
) {
  const { query, setQuery } = useTableRouter(
    getParamsToQuery,
    getParamsFromQuery,
    setURL,
    undefined,
    setURL !== false,
  );
  const search = ref<string | undefined>(setURL !== false ? query.value.search : undefined);
  const setSearch = (value?: string) => {
    search.value = value;
  };
  const searchQuery = computed(() => query.value.search);
  watch(searchQuery, () => {
    if (setURL !== false) {
      search.value = searchQuery.value;
    }
  });

  const onSorting = (column: string, order?: 'asc' | 'desc' | 'ASC' | 'DESC') => {
    const normalized = order?.toLowerCase() as 'asc' | 'desc' | undefined;
    setQuery({
      ...query.value,
      page: 1,
      orderBy: normalized ? [{
        column,
        order: normalized === 'asc' ? SortOrder.Asc : SortOrder.Desc,
      }] : undefined,
    });
  };
  const onPage = (newPage: number) => {
    setQuery({
      ...query.value,
      page: newPage,
    });
  };
  const onSearch = () => {
    setQuery({
      ...query.value,
      search: search.value,
      page: 1,
      order: undefined,
    });
  };

  return {
    query,
    setQuery,
    search,
    setSearch,
    onSearch,
    onSorting,
    onPage,
  };
}
